import { useEffect } from 'react';

/** Attribute marking the palette's own subtree, which the shortcut stays live inside. */
export const COMMAND_PALETTE_ROOT_ATTR = 'data-command-palette';

/**
 * Typing in a form control swallows the shortcut — otherwise Ctrl K fires while
 * someone drafts a caption. The palette's own input is the exception, so the
 * shortcut still toggles the palette shut from inside it.
 */
function isTypingTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  if (target.closest(`[${COMMAND_PALETTE_ROOT_ATTR}]`)) return false;
  if (target.isContentEditable) return true;

  return target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.tagName === 'SELECT';
}

/**
 * Ctrl K / Cmd K anywhere in the authed shell. `onToggle` must be stable —
 * wrap it in `useCallback` — or the listener re-subscribes on every render.
 */
export function useCommandPaletteShortcut(onToggle: () => void): void {
  useEffect(() => {
    function handleKeyDown(event: KeyboardEvent) {
      if (event.key !== 'k' && event.key !== 'K') return;
      if (!(event.ctrlKey || event.metaKey) || event.altKey) return;
      if (isTypingTarget(event.target)) return;

      // Firefox binds Cmd/Ctrl K to the browser search bar.
      event.preventDefault();
      onToggle();
    }

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onToggle]);
}
